import { PAGINATION } from '@hygge/shared';
import { api } from './api';
import type {
  ProjectListItem,
  ProjectDetail,
  ProjectMemberItem,
  ProjectStats,
  ProjectActivityItem,
  WeeklyRosterResponse,
  RosterForecastResponse,
  ForecastSummaryResponse,
  PlanVsActualResponse,
  ReopenPeriodBody,
  ReopenPeriodResult,
} from '@hygge/shared';

export interface ProjectsQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  source?: string;
  managerId?: string;
  sort?: string;
  order?: string;
}

/**
 * What `/projects` is asked for when a screen offers projects to FILTER BY.
 *
 * `/projects` pages its own answer: `limit` defaults to 20 (`PaginationQueryDto`),
 * so a caller that sends nothing gets page one by name and no sign that the rest
 * exist. Measured on dev on 2026-09-14 that was 20 of 26 — the six alphabetically
 * last projects could not be filtered for at all. [HW-903]
 *
 * The Time Tracker's report filters — the Reports tab's and the Dashboard's
 * Project filters and the Report Builder's project sources — send this query
 * to `/time-entries/report-projects` instead (`useReportProjects`), which takes
 * the same query and answers with the same page of filter fields only: every
 * project to a reader whose reports read the whole company, the projects
 * `/projects` would list to anyone else. [HW-972]
 *
 * `MAX_LIMIT` is the largest page the endpoint accepts, not a preference: the
 * DTO's `@Max` refuses more with a 400, and every caller here renders a failed
 * list as an empty one. Its ceiling is a backend-local constant, so this pins
 * the same NUMBER and not the same definition — raise the shared one past the
 * endpoint's and every list pinned to it empties in silence, this filter and the
 * active-projects picker alike. That is what the literal `100` in the test next
 * door is there to catch. Above 100 projects the silence of HW-903 returns one
 * ceiling higher and the list would need a search rather than a longer page;
 * nothing here reads `meta.total` to say so.
 *
 * No `status`: the endpoint filters by it only when asked, and a filter over
 * hours already recorded must still be able to offer archived and on-hold
 * projects — they hold the hours somebody logged against them, and a report
 * about one is a fair question. The filters list them behind a "Show archived"
 * toggle rather than by default (`lib/archived-projects.ts`, HW-951), which is
 * why the fetch still asks for every status. That is the opposite of the picker
 * that chooses where NEW hours go, which is narrowed to active projects on the
 * server (`TIME_TRACKING_PROJECT_QUERY`, HW-874).
 *
 * Nor is a full page the same as every project the reader has hours on. A caller
 * holding none of `projects.view`, `projects.view.all`, `projects.*` or `*` —
 * `projects.view.assigned` alone — is narrowed server-side to their CURRENT
 * memberships (`projects.service.ts` — `getMemberProjectIds`, `removedAt: null`),
 * while their own entries stay readable whatever became of the membership
 * (`time-entries.service.ts` keys those on `employeeId` and joins no membership).
 * Measured locally on 2026-09-14: an employee offered 2 projects in this filter
 * held 13 entries across 4. That gap is older than this constant and no page
 * size closes it. On the report filters a reader whose reports read the whole
 * company is offered every project by the Time Tracker's route; for everyone
 * else the gap stands. [HW-972]
 *
 * `sort`/`order` are spelled out even though `name asc` is the endpoint's own
 * default: they reproduce, exactly, the literal the Report Builder's filter
 * source carried before it read this constant, so nothing about what the Builder
 * asks for, or which key it lands on, moved when it did. They also keep this key
 * apart from `vacancy-details-tab.tsx`'s `{ limit: 100 }`, which fetches the same
 * rows under a key of its own. They are NOT what makes the screens share a
 * request: the key is hashed structurally, so separate copies of one literal
 * dedupe just as well — and did, before this constant existed.
 */
export const PROJECT_FILTER_QUERY = {
  limit: PAGINATION.MAX_LIMIT,
  sort: 'name',
  order: 'asc',
} as const satisfies ProjectsQueryParams;

export interface ProjectsResponse {
  data: ProjectListItem[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface ProjectActivityResponse {
  data: ProjectActivityItem[];
  nextCursor: string | null;
}

export async function getProjects(params: ProjectsQueryParams = {}): Promise<ProjectsResponse> {
  const { data } = await api.get<ProjectsResponse>('/projects', { params });
  return data;
}

export async function getProject(id: string): Promise<ProjectDetail> {
  const { data } = await api.get<ProjectDetail>(`/projects/${id}`);
  return data;
}

export async function getProjectStats(): Promise<ProjectStats> {
  const { data } = await api.get<ProjectStats>('/projects/stats');
  return data;
}

export async function createProject(body: {
  name: string;
  slug: string;
  description?: string;
  managerIds: string[];
  status?: string;
  memberIds?: string[];
  color?: string;
}): Promise<ProjectDetail> {
  const { data } = await api.post<ProjectDetail>('/projects', body);
  return data;
}

export async function updateProject(
  id: string,
  body: {
    name?: string;
    description?: string | null;
    /** The project's managers, exactly; omit to leave them as they are. */
    managerIds?: string[];
    status?: string;
    color?: string;
    /** The project's billable default; time entries inherit it. */
    isBillable?: boolean;
    /** 'outstaff' | 'outsource'; the Start Timer form branches on it. */
    engagementType?: string;
    /** With `status: 'archived'`: what to do with the project's boards. */
    archiveBoards?: ArchiveBoardChoice;
  },
): Promise<ProjectDetail> {
  const { data } = await api.patch<ProjectDetail>(`/projects/${id}`, body);
  return data;
}

/**
 * What archiving a project does to its boards (HW-983), among those whose
 * settings the caller may change: `archive` marks them archived and leaves them
 * linked; `unlink` leaves their status as it is and unlinks them. Any other
 * linked board, and every board when this is omitted, is left as it is.
 */
export type ArchiveBoardChoice = 'archive' | 'unlink';

export async function archiveProject(id: string, boards?: ArchiveBoardChoice): Promise<void> {
  await api.delete(`/projects/${id}`, { params: boards ? { boards } : undefined });
}

export async function getProjectMembers(projectId: string): Promise<ProjectMemberItem[]> {
  const { data } = await api.get<ProjectMemberItem[]>(`/projects/${projectId}/members`);
  return data;
}

/** Reopen a closed period for one member of the project. [HW-963] */
export async function reopenMemberPeriod(
  projectId: string,
  employeeId: string,
  body: ReopenPeriodBody,
): Promise<ReopenPeriodResult> {
  const { data } = await api.post<ReopenPeriodResult>(
    `/projects/${projectId}/members/${employeeId}/reopen`,
    body,
  );
  return data;
}

export async function addProjectMembers(
  projectId: string,
  employeeIds: string[],
): Promise<ProjectMemberItem[]> {
  const { data } = await api.post<ProjectMemberItem[]>(`/projects/${projectId}/members`, {
    employeeIds,
  });
  return data;
}

export async function updateProjectMember(
  projectId: string,
  employeeId: string,
  data: { joinedAt?: string; removedAt?: string | null; isManager?: boolean },
): Promise<void> {
  await api.patch(`/projects/${projectId}/members/${employeeId}`, data);
}

export async function removeProjectMember(projectId: string, employeeId: string): Promise<void> {
  await api.delete(`/projects/${projectId}/members/${employeeId}`);
}

export async function reorderProjectMembers(
  projectId: string,
  employeeIds: string[],
): Promise<void> {
  await api.patch(`/projects/${projectId}/members/order`, { employeeIds });
}

export async function generateSlug(name: string): Promise<string> {
  const { data } = await api.get<{ slug: string }>('/projects/slug/generate', {
    params: { name },
  });
  return data.slug;
}

export async function getProjectActivity(
  projectId: string,
  limit = 20,
  cursor?: string,
): Promise<ProjectActivityResponse> {
  const { data } = await api.get<ProjectActivityResponse>(`/projects/${projectId}/activity`, {
    params: { limit, cursor },
  });
  return data;
}

export async function getRoster(projectId: string, month: string): Promise<WeeklyRosterResponse> {
  const { data } = await api.get<WeeklyRosterResponse>(`/projects/${projectId}/roster`, {
    params: { month },
  });
  return data;
}

export async function upsertRoster(
  projectId: string,
  month: string,
  week: number,
  entries: {
    employeeId: string;
    realHours?: number;
    billableHours?: number;
    niceToHaveRate?: number;
    realRate?: number;
  }[],
): Promise<WeeklyRosterResponse> {
  const { data } = await api.put<WeeklyRosterResponse>(`/projects/${projectId}/roster`, {
    month,
    week,
    entries,
  });
  return data;
}

export function getRosterExportUrl(projectId: string, month: string, format = 'csv'): string {
  return `/api/v1/projects/${projectId}/roster/export?month=${month}&format=${format}`;
}

export interface FinancialsSummary {
  month: string;
  totalRevenue: number;
  totalCost: number;
  grossMargin: number;
  utilization: number;
  totalRealHours: number;
  totalBillableHours: number;
  memberCount: number;
  forecast: {
    forecastRevenue: number;
    forecastCost: number;
    revenueVariance: number;
    elapsedBusinessDays: number;
    totalBusinessDays: number;
  } | null;
  monthlyBreakdown: { month: string; revenue: number; cost: number }[];
}

export async function getFinancials(projectId: string, month: string): Promise<FinancialsSummary> {
  const { data } = await api.get<FinancialsSummary>(`/projects/${projectId}/financials`, {
    params: { month },
  });
  return data;
}

export async function getRosterForecast(
  projectId: string,
  month?: string,
): Promise<RosterForecastResponse> {
  const { data } = await api.get<RosterForecastResponse>(`/projects/${projectId}/roster-forecast`, {
    params: month ? { month } : {},
  });
  return data;
}

export async function upsertRosterForecast(
  projectId: string,
  body: {
    month: string;
    entries: {
      employeeId: string;
      realHours?: number;
      billableHours?: number;
      niceToHaveRate?: number;
      realRate?: number;
    }[];
  },
): Promise<RosterForecastResponse> {
  const { data } = await api.put<RosterForecastResponse>(
    `/projects/${projectId}/roster-forecast`,
    body,
  );
  return data;
}

export async function getForecastSummary(projectId: string): Promise<ForecastSummaryResponse> {
  const { data } = await api.get<ForecastSummaryResponse>(`/projects/${projectId}/forecast/summary`);
  return data;
}

export async function getPlanVsActual(projectId: string, month: string): Promise<PlanVsActualResponse> {
  const { data } = await api.get<PlanVsActualResponse>(`/projects/${projectId}/plan-vs-actual`, {
    params: { month },
  });
  return data;
}
